import React, { useState, useEffect } from 'react';
import '../Reports/Challenge.css';
import { CSVLink } from 'react-csv';
import { useLocation, Link, useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";

const PlayerAttendance = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const location = useLocation();
  const [Attendance, setAttendance] = useState([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const playerId = searchParams.get("playerId");
  const batchId = searchParams.get("batchId");

  const token = localStorage.getItem('auth-token');
  useEffect(() => {
    if (!playerId) return;

    const fetchAttendance = async () => {
      setLoading(true);
      setError(null);
      try {
        const url = batchId
          ? `${process.env.REACT_APP_API_BATCH_PLAYER_REPORT}/${batchId}/player/${playerId}/monthly-attendance`
          : `${process.env.REACT_APP_API_PLAYER_REPORT}/${playerId}/batchwise-attendance`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            'Authorization': `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch attendance data");
        }

        const data = await response.json();
        const monthNames = [
          "January", "February", "March", "April", "May", "June",
          "July", "August", "September", "October", "November", "December"
        ];

        if (data.success) {
          let formattedData = [];

          if (batchId && data.data.batchAttendance) {
            formattedData = data.data.batchAttendance.map((item) => ({
              batchName: item.batch.name,
              batchId: item.batch.id,
              playerName: item.player.user.name,
              playerId: item.player.id,
              month: item.month,
              monthName: monthNames[item.month - 1],
              year: item.year,
              attendance: `${item.totalPlayerAttendence}/${item.totalSessionTracked}`,
              compAttendance: item.compClasses,
            }));
          } else if (data.data.batchAttendence) {
            formattedData = data.data.batchAttendence.map((item) => ({
              batchName: item.batch.name,
              batchId: item.batch.id,
              playerName: item.player.user.name,
              playerId: item.player.id,
              month: item.month,
              monthName: monthNames[item.month - 1],
              year: item.year,
              attendance: `${item.totalPlayerAttendence}/${item.totalSessionTracked}`,
              compAttendance: item.compClasses,
            }));
          } else {
            throw new Error("Invalid response structure");
          }

          setAttendance(formattedData);
        } else {
          setError("Invalid response structure");
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAttendance();
  }, [playerId, batchId]);

  const headers = [
    { label: "Batch Name", key: "batchName" },
    { label: "Player Name", key: "playerName" },
    { label: "Month", key: "month" },
    { label: "Year", key: "year" },
    { label: "Attendance", key: "attendance" },
    { label: "Comp. Attendance", key: "compAttendance" }
  ];


  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setrowsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const filteredAttendance = Attendance.filter((Attend) =>
    Object.values(Attend)
      .join(' ')
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );
  const handleSort = (columnKey) => {
    let direction = 'asc';
    if (sortConfig.key === columnKey && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: columnKey, direction });
  };

  const sortedAttendance = [...filteredAttendance].sort((a, b) => {
    if (sortConfig.key === '') return 0;

    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];

    if (sortConfig.direction === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
    }
  });
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = sortedAttendance.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredAttendance.length / rowsPerPage);

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prevPage) => prevPage + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prevPage) => prevPage - 1);
  };

  const generatePageNumbers = () => {
    const pages = [];
    const totalPagesToShow = 5;

    if (totalPages <= totalPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const renderPagination = () => {
    return generatePageNumbers().map((page, index) =>
      typeof page === 'number' ? (
        <button
          key={index}
          className={`page-number ${currentPage === page ? 'active' : ''}`}
          onClick={() => handlePageClick(page)}
        >
          {page}
        </button>
      ) : (
        <span key={index} className="pagination-dots">
          {page}
        </span>
      )
    );
  };
  const handleItemsPerPageChange = (event) => {
    setrowsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };
  const renderSortArrow = (columnKey) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };
  const totalEntries = filteredAttendance.length;
  const indexOfFirstEntry = totalEntries > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const indexOfLastEntry = totalEntries > 0 ? Math.min(currentPage * rowsPerPage, totalEntries) : 0;

  const handlePlayerClick = (event, playerId, batchId, month, year) => {
    const url = `/detailsAttendance?playerId=${playerId}&batchId=${batchId}&month=${month}&year=${year}`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };


  return (
    <div className="enrollment-container">
      <div className="breadcrumb">
        <Link to="/playerBatch" className="breadcrumb-link">Player Batch</Link> &gt; Player Attendance
      </div>
      <div className="search-bar">
        <div className="input-container">
          <input
            type="text"
            id="searchInput"
            placeholder=""
            className={`search-input ${searchTerm ? "has-value" : ""}`}
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <label htmlFor="searchInput" className="floating-label">
            🔍 Search
          </label>
        </div>
        <CSVLink
          data={filteredAttendance}
          headers={headers}
          filename="PlayerAttendance.csv"
          className="btn btn-primary no-decoration"
        >
          <button className="csv_button" type="button" >
            <span
              className="button__text"

            >
              Export CSV
            </span>
            <span className="button__icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 35 35" className="svg">
                <path d="M17.5,22.131a1.249,1.249,0,0,1-1.25-1.25V2.187a1.25,1.25,0,0,1,2.5,0V20.881A1.25,1.25,0,0,1,17.5,22.131Z"></path>
                <path d="M17.5,22.693a3.189,3.189,0,0,1-2.262-.936L8.487,15.006a1.249,1.249,0,0,1,1.767-1.767l6.751,6.751a.7.7,0,0,0,.99,0l6.751-6.751a1.25,1.25,0,0,1,1.768,1.767l-6.752,6.751A3.191,3.191,0,0,1,17.5,22.693Z"></path>
                <path d="M31.436,34.063H3.564A3.318,3.318,0,0,1,.25,30.749V22.011a1.25,1.25,0,0,1,2.5,0v8.738a.815.815,0,0,0,.814.814H31.436a.815.815,0,0,0,.814-.814V22.011a1.25,1.25,0,1,1,2.5,0v8.738A3.318,3.318,0,0,1,31.436,34.063Z"></path>
              </svg>
            </span>
          </button>
        </CSVLink>
      </div>
      <select className="items-per-page-select" value={rowsPerPage} onChange={handleItemsPerPageChange}>
        <option value={10}>10 items per page</option>
        <option value={50}>50 items per page</option>
        <option value={75}>75 items per page</option>
        <option value={100}>100 items per page</option>
      </select>
      <div className="pagination-selector">
        <div className="button-group">
          <div className="group-label">Items per page:</div>
          <div className="button-options">
            {[10, 50, 75, 100].map((option) => (
              <button
                key={option}
                onClick={() =>
                  handleItemsPerPageChange({ target: { value: option } })
                }
                className={`page-button ${rowsPerPage === option ? 'active' : ''}`}
              >
                <span className="button-text">{option}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
      {loading && (
        <div className="premium-loader">
          <div className="progress-track">
            <div className="progress-bar"></div>
          </div>
          <div className="status-text">
            <span>L</span>
            <span>O</span>
            <span>A</span>
            <span>D</span>
            <span>I</span>
            <span>N</span>
            <span>G</span>
          </div>
        </div>
      )}
      {error && <div className="error-message">{error}</div>}
      {!loading && !error && filteredAttendance.length === 0 && (
        <div className="error-message">No data available</div>
      )}
      <div className="payment-list-scroll-container" style={{ overflowY: 'auto' }}>
        <table className="payment-list-table">
          <thead>
            <tr className="challenges-header-row">
              {headers.map((header) => (
                <th
                  key={header.key}
                  className="challenges-column"
                  onClick={() => handleSort(header.key)}
                >
                  {header.label} {renderSortArrow(header.key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentRows.map((Attend) => (
              <tr key={Attend.id} className="challenges-row">
                <td className="challenges-cell">{Attend.batchName}</td>
                <td className="challenges-cell">
                  <span
                    className="player-link"
                    onClick={(event) =>
                      handlePlayerClick(event, Attend.playerId, Attend.batchId, Attend.month, Attend.year)
                    }
                    style={{ cursor: 'pointer', color: 'blue', textDecoration: 'none' }}
                  >
                    {Attend.playerName}
                  </span>

                </td>
                <td className="challenges-cell">{Attend.monthName}</td>
                <td className="challenges-cell">{Attend.year}</td>
                <td className="challenges-cell">{Attend.attendance}</td>
                <td className="challenges-cell">{Attend.compAttendance}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="pagination">
        <div className="showing-entries">
          Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
        </div>
        <button
          className="prev-button"
          onClick={handlePrevPage}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        {renderPagination()}
        <button
          className="next-button"
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default PlayerAttendance;
